import { useScrollAnimation } from "../../hooks/useScrollAnimation";

// Device Frame Component
const DeviceFrame = ({ title, children }) => (
  <div className="relative rounded-[12px] overflow-hidden border border-[#1e1e1e] bg-[#141414]">
    <div className="bg-[#1a1a1a] px-3 py-2 flex items-center gap-2 border-b border-[#1e1e1e]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#ef4444]"></div>
      <div className="w-2.5 h-2.5 rounded-full bg-[#f59e0b]"></div>
      <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]"></div>
      {title && (
        <span className="ml-2 text-[10px] text-[#6b7280]">{title}</span>
      )}
    </div>
    <div className="relative">
      {children}
      {/* Bottom fade gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, transparent 50%, rgba(10, 10, 10, 0.6) 75%, rgba(10, 10, 10, 1) 100%)",
        }}
      />
    </div>
  </div>
);

// Map View Screenshot - dense dot cluster map
const MapViewScreenshot = () => (
  <DeviceFrame title="Veranda — Market View">
    <div className="h-[280px] relative overflow-hidden">
      <img
        src="https://customer-assets.emergentagent.com/job_home-scoring-kit/artifacts/8z8m5v1p_Screenshot%202026-03-19%20at%209.52.41%E2%80%AFAM.png"
        alt="Veranda AI Map View - Dense property clusters in South Portland, ME"
        className="w-full h-full object-cover"
      />
    </div>
  </DeviceFrame>
);

// Property Card Screenshot - Detailed view with score breakdown
const PropertyCardScreenshot = () => (
  <DeviceFrame title="Property Detail">
    <div className="bg-[#0a0a0a] p-3 h-[280px]">
      <div className="bg-[#141414] border border-[#1e1e1e] rounded-lg overflow-hidden p-3">
        {/* Top row - Image + Address */}
        <div className="flex gap-3 mb-3">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <img
              src="https://images.unsplash.com/photo-1572120360610-d971b9d7767c?crop=entropy&cs=srgb&fm=jpg&w=400&q=80"
              alt="Older home with weathered siding"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-white text-[13px] font-semibold">142 Maple Ave</p>
            <p className="text-[#6b7280] text-[9px] mt-0.5">
              Clapboard / Wood Shingle · 1930
            </p>
          </div>
        </div>

        {/* Score breakdown cards */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-[#1a1a1a] rounded-lg p-2 text-center">
            <p className="text-[#6b7280] text-[7px] uppercase tracking-wide mb-1">Need</p>
            <p className="text-white text-[18px] font-bold">76</p>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-2 text-center">
            <p className="text-[#6b7280] text-[7px] uppercase tracking-wide mb-1">Capacity</p>
            <p className="text-white text-[18px] font-bold">77</p>
          </div>
          <div className="bg-[#1a1a1a] rounded-lg p-2 text-center">
            <p className="text-[#6b7280] text-[7px] uppercase tracking-wide mb-1">Motivation</p>
            <p className="text-white text-[18px] font-bold">88</p>
          </div>
        </div>

        {/* Lead Score - Bottom */}
        <div className="border-t border-[#1e1e1e] pt-3 flex items-center justify-between">
          <span className="text-[#6b7280] text-[9px] uppercase tracking-wide">Lead Score</span>
          <div className="flex items-center gap-2">
            <span className="text-white text-[28px] font-bold leading-none">86</span>
            <span className="text-[9px] font-semibold px-2 py-1 rounded bg-[#ef4444] text-white">HIGH</span>
          </div>
        </div>
      </div>
    </div>
  </DeviceFrame>
);

// Chat Interface Screenshot
const ChatInterfaceScreenshot = () => {
  const leads = [
    { address: "142 Maple Ave", score: 83, material: "Wood Shingle", age: "71 yrs", value: "$357K", owner: "Sarah Mitchell" },
    { address: "58 Harbor Rd", score: 80, material: "Vinyl Siding", age: "126 yrs", value: "$780K", owner: "James Calloway" },
    { address: "315 Birch Ln", score: 80, material: "Wood Shingle", age: "88 yrs", value: "$360K", owner: "Patricia Huang" },
    { address: "27 Elm Ct", score: 78, material: "Clapboard", age: "94 yrs", value: "$425K", owner: "Robert Nilsson" },
    { address: "891 Cedar St", score: 77, material: "Aluminum", age: "62 yrs", value: "$290K", owner: "Maria Santos" },
  ];

  return (
    <DeviceFrame title="Veranda — Chat">
      <div className="bg-[#0a0a0a] p-3 h-[280px] overflow-hidden">
        {/* User message */}
        <div className="flex justify-end mb-3">
          <div className="bg-[#3b82f6] text-white text-[10px] px-3 py-1.5 rounded-lg max-w-[80%]">
            show me the top 5 highest-scoring leads
          </div>
        </div>

        {/* AI response */}
        <div className="bg-[#141414] border border-[#1e1e1e] rounded-lg p-2.5">
          <p className="text-white text-[10px] font-medium mb-0.5">Top 5 Highest-Scoring Leads</p>
          <p className="text-[#6b7280] text-[8px] mb-2">Properties with the greatest siding replacement urgency</p>

          {/* Mini table */}
          <div>
            {/* Column headers */}
            <div className="flex items-center gap-2 text-[7px] py-1 border-b border-[#1e1e1e]">
              <span className="text-[#6b7280] font-medium w-20 uppercase tracking-wide">Address</span>
              <span className="text-[#6b7280] font-medium w-6 uppercase tracking-wide">Score</span>
              <span className="text-[#6b7280] font-medium w-16 uppercase tracking-wide">Material</span>
              <span className="text-[#6b7280] font-medium w-10 uppercase tracking-wide">Age</span>
              <span className="text-[#6b7280] font-medium w-10 uppercase tracking-wide">Value</span>
            </div>
            {/* Data rows */}
            {leads.map((lead, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[8px] py-1 border-b border-[#1e1e1e] last:border-0">
                <span className="text-white font-medium w-20 truncate">{lead.address}</span>
                <span className="text-[#3b82f6] font-semibold w-6">{lead.score}</span>
                <span className="text-[#6b7280] w-16 truncate">{lead.material}</span>
                <span className="text-[#6b7280] w-10">{lead.age}</span>
                <span className="text-[#9ca3af] w-10">{lead.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DeviceFrame>
  );
};

// Product Section Data
const columns = [
  {
    screenshot: <MapViewScreenshot />,
    title: "See the whole market",
    description: "Every property in your territories, enriched with deep data. Find markets where opportunity is most ripe.\u00A0",
  },
  {
    screenshot: <PropertyCardScreenshot />,
    title: "Score every home",
    description: "AI assesses available imagery. Financial and behavioral signals layer in. Every house gets a score.",
  },
  {
    screenshot: <ChatInterfaceScreenshot />,
    title: "Questions? Ask away.",
    description: "Breakthrough insights as easy as texting. Ask how to plan a sales trip, what neighborhoods to hit, or for custom lists.",
  },
];

export const ProductSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <section
      className="py-24 px-[5%] border-t border-[#1e1e1e]"
      data-testid="product-section"
    >
      <div className="max-w-content mx-auto">
        <div
          ref={headerRef}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
          }}
        >
          <p className="text-[0.7rem] font-semibold tracking-[3px] uppercase text-[#6b7280] mb-6">
            THE PRODUCT
          </p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-medium tracking-[-0.5px] leading-[1.2] text-white mb-4 max-w-3xl">
            Property-level demand intelligence. For{" "}
            <span className="text-[#3b82f6]">every home</span> in your market.
          </h2>
          <p className="text-[1rem] text-[#9ca3af] mb-16 max-w-2xl">
            Veranda analyzes every property and predicts how likely they are to
            convert — based on product condition, owner financial capacity, and
            motivation to buy.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {columns.map((col, idx) => (
            <div 
              key={idx} 
              data-testid={`product-card-${idx}`}
              style={{
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? "translateY(0)" : "translateY(30px)",
                transition: `opacity 0.7s ease-out ${0.1 + idx * 0.15}s, transform 0.7s ease-out ${0.1 + idx * 0.15}s`,
              }}
            >
              <div className="mb-4">{col.screenshot}</div>
              <h3 className="text-[1.1rem] font-semibold text-white mb-2">
                {col.title}
              </h3>
              <p className="text-[16px] md:text-[0.875rem] text-[#9ca3af] leading-relaxed">
                {col.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
